import { get4QueryKey, useSendInvoice, useSetInvoice } from "@synthesis/shared-services/internal/payment/hooks";
import { Button, Input, Modal, setPromiseSnack } from "@synthesis/shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { usePageStore } from "../../application-detail-page.lib";
import type { IssueInvoicePopupAction } from "../application-control-popup.lib";

const INVOICE_SNACK_MESSAGES = {
  pending: "Выполняется...",
  fulfilled: "Счёт выставлен",
  rejected: "Не удалось выставить счёт. Попробуйте еще раз",
} as const;

const INVOICE_AMOUNT_MASK = { mask: /^\d{0,12}(,\d{0,2})?$/ };
const INVOICE_NUMBER_MASK = { mask: /^\d{0,20}$/ };

const paymentInvoiceFormSchema = z.object({
  amount: z
    .string()
    .min(1, "Укажите стоимость по счёту")
    .refine((value) => Number(value.replace(",", ".")) > 0, "Стоимость по счёту должна быть больше нуля"),
  invoiceNumber: z.string().min(1, "Укажите номер счёта"),
});

type PaymentInvoiceFormValues = z.infer<typeof paymentInvoiceFormSchema>;

const IssueInvoiceModal = observer(({ popupAction }: { popupAction: IssueInvoicePopupAction }) => {
  const store = usePageStore();
  const queryClient = useQueryClient();
  const setInvoiceMutation = useSetInvoice({
    mutation: {
      onSuccess: (updatedView) => {
        queryClient.setQueryData(get4QueryKey({ path: { id: popupAction.requestId } }), updatedView);
        store?.setPopupAction(null);
      },
    },
  });
  const sendInvoiceMutation = useSendInvoice({
    mutation: {
      onSuccess: (updatedView) => {
        queryClient.setQueryData(get4QueryKey({ path: { id: popupAction.requestId } }), updatedView);
        store?.setPopupAction(null);
      },
    },
  });

  const methods = useForm<PaymentInvoiceFormValues>({
    resolver: zodResolver(paymentInvoiceFormSchema),
    defaultValues: { amount: "", invoiceNumber: "" },
    mode: "onChange",
  });

  const isPending = setInvoiceMutation.isPending;

  useEffect(() => {
    methods.reset({ amount: "", invoiceNumber: "" });
  }, [methods, popupAction]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      store?.setPopupAction(null);
    }
  };

  const handleSubmit = methods.handleSubmit((formData) => {
    const action = async () => {
      const paymentView = await setInvoiceMutation.mutateAsync({
        path: { id: popupAction.requestId },
        body: {
          amount: Number(formData.amount.replace(",", ".")),
          invoiceNumber: formData.invoiceNumber,
        },
      });

      if (paymentView.invoiceNumber) {
        await sendInvoiceMutation.mutateAsync({
          path: { id: popupAction.requestId },
        });
      }
    };

    setPromiseSnack({
      promise: action(),
      promiseSettings: {
        title: INVOICE_SNACK_MESSAGES,
      },
    });
  });

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Выставление счёта</Modal.Title>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-6 p-8">
              <h3 className="heading-h4 text-type-common">Заполните реквизиты счета для отправки заявителю</h3>

              <FormProvider {...methods}>
                <div className="flex gap-4">
                  <Input
                    className="flex-1"
                    name="amount"
                    label="Стоимость по счёту, ₽"
                    required
                    disabled={isPending}
                    settings={INVOICE_AMOUNT_MASK}
                    placeholder="Введите сумму"
                  />
                  <Input
                    className="flex-1"
                    name="invoiceNumber"
                    label="Номер счёта"
                    required
                    disabled={isPending}
                    settings={INVOICE_NUMBER_MASK}
                    placeholder="Введите номер счета"
                  />
                </div>
              </FormProvider>
            </div>
          </div>

          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
            <Button type="submit" theme="primary" size="lg" disabled={isPending || !methods.formState.isValid}>
              Применить
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isPending} onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
});

export const PaymentInvoiceModal = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "issue-invoice") {
    return null;
  }

  return <IssueInvoiceModal popupAction={popupAction} />;
});
